import { useRef, useState } from 'react'

/*
 * Le glissement latéral d'une ligne de liste.
 *
 * Extrait de `TransactionRow` le 2026-09-05, quand les notifications ont eu
 * besoin du même geste. Ce n'est pas de la mutualisation de confort : chaque
 * ligne de commentaire ci-dessous répare un bug qui n'apparaissait que sur un
 * vrai appareil, et une seconde copie les aurait tous ramenés un par un.
 *
 * Le contrat est volontairement pauvre : le hook dit de combien la ligne a
 * bougé et tranche à la fin du geste. Ce qu'on peint derrière, et ce que le
 * geste déclenche, reste l'affaire de l'appelant.
 */

/** De combien il faut tirer pour qu'un geste compte comme un raccourci. */
export const SWIPE_THRESHOLD = 72
/** Au-delà, la ligne ne suit plus le doigt : le geste est acquis. */
export const SWIPE_MAX = 96

export interface SwipeOptions {
  /** Faux = la ligne reste inerte, aucun geste ne s'engage. */
  enabled: boolean
  /** Geste validé vers la gauche. */
  onLeft?: () => void
  /** Geste validé vers la droite. */
  onRight?: () => void
}

export interface Swipe {
  /** Déplacement courant, en pixels. Négatif vers la gauche. */
  dx: number
  /** Le seuil est franchi : relâcher maintenant validerait le geste. */
  armed: boolean
  /**
   * Un geste vient-il de se terminer ? À appeler dans le `onClick` de la ligne,
   * qui doit alors ne RIEN faire.
   *
   * L'appel consomme le drapeau : une activation au clavier n'émet aucun
   * `pointerdown` pour le remettre à zéro, et resterait sinon bloquée.
   */
  consume: () => boolean
  handlers: {
    onPointerDown: (event: React.PointerEvent) => void
    onPointerMove: (event: React.PointerEvent) => void
    onPointerUp: () => void
    onPointerCancel: () => void
  }
}

export function useSwipe({ enabled, onLeft, onRight }: SwipeOptions): Swipe {
  const [dx, setDx] = useState(0)
  const drag = useRef<{ x0: number; y0: number; actif: boolean } | null>(null)
  /*
   * Le déplacement, EN DOUBLE.
   *
   * L'état peint la ligne ; le ref sert à décider. Trancher sur l'état
   * reviendrait à lire le rendu précédent — la famille de bug la plus fréquente
   * de ce code.
   */
  const dxRef = useRef(0)
  /*
   * Un geste vient-il de se terminer sur cette ligne ?
   *
   * Un REF et non l'état `dx`, parce que le navigateur émet le `click` APRÈS le
   * `pointerup` : celui-ci a déjà remis `dx` à zéro et React a déjà réaffiché,
   * si bien qu'un test sur `dx` était toujours vrai. Le glissement supprimait
   * ou corrigeait, ET ouvrait la feuille de détail par-dessus.
   */
  const glisse = useRef(false)

  function move(value: number) {
    dxRef.current = value
    setDx(value)
  }

  function onPointerDown(event: React.PointerEvent) {
    if (!enabled || event.button !== 0) return
    // Toute nouvelle pression repart d'un geste vierge : sans ça, un glissement
    // dont le `click` ne serait jamais venu avalerait l'appui suivant.
    glisse.current = false
    drag.current = { x0: event.clientX, y0: event.clientY, actif: false }
  }

  function onPointerMove(event: React.PointerEvent) {
    const d = drag.current
    if (!d) return
    const ecartX = event.clientX - d.x0
    const ecartY = event.clientY - d.y0

    if (!d.actif) {
      // Le geste ne s'engage qu'une fois l'horizontale plus franche que la
      // verticale — sans quoi le moindre défilement de la liste ferait glisser
      // la ligne sous le doigt.
      if (Math.abs(ecartX) < 10 || Math.abs(ecartX) <= Math.abs(ecartY)) return
      d.actif = true
      // Dès l'engagement, et non à la validation : même un geste abandonné
      // sous le seuil est un glissement, pas un appui — il ne doit rien ouvrir.
      glisse.current = true
      /*
       * La capture est un CONFORT — elle garde les événements si le doigt sort
       * de la ligne — pas une nécessité : sans elle, les `pointermove`
       * continuent d'arriver tant que le doigt reste dessus.
       *
       * Elle lève `NotFoundError` quand le pointeur n'est plus actif, et
       * l'exception tuait tout le geste avant même le premier déplacement :
       * la ligne ne bougeait pas d'un pixel, sans la moindre trace.
       */
      try { event.currentTarget.setPointerCapture(event.pointerId) } catch { /* tant pis */ }
    }
    // Freiné au-delà du seuil : le geste est acquis, tirer plus n'apporte rien
    // et une ligne qui traverse l'écran donne l'impression qu'elle s'en va.
    move(Math.max(-SWIPE_MAX, Math.min(SWIPE_MAX, ecartX)))
  }

  /** Le doigt est parti : on tranche. `annule` = le geste est perdu, pas validé. */
  function end(annule: boolean) {
    const engage = drag.current?.actif ?? false
    const franchi = Math.abs(dxRef.current) >= SWIPE_THRESHOLD
    const sens = dxRef.current
    drag.current = null
    move(0)
    // `pointercancel` veut dire que le geste a été REPRIS par le système —
    // l'exécuter reviendrait à valider une action que l'utilisateur n'a jamais
    // fini de demander.
    if (annule || !engage || !franchi) return
    if (sens < 0) onLeft?.()
    else onRight?.()
  }

  return {
    dx,
    armed: Math.abs(dx) >= SWIPE_THRESHOLD,
    consume: () => {
      if (!glisse.current) return false
      glisse.current = false
      return true
    },
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: () => end(false),
      onPointerCancel: () => end(true),
    },
  }
}
